/**
 * #166 — Pure predicate driving the WO Workorder drag-drop confirmation modal.
 *
 * --------------------------------------------------------------------------
 * WHY THIS FILE EXISTS (for code reviewers asking "is this overkill?"):
 * --------------------------------------------------------------------------
 *
 * Same pattern as `isRepairOrderWithInstaller`, `isUnassignedOpenWork`,
 * `buildDragNDropUpdateFields`, `datesEqual`: change-detection / drop-
 * decision logic gets its own pure function so tests import the EXACT
 * production predicate (no test-helper drift, #73 / #80 / #83 / #84 / #85).
 *
 * --------------------------------------------------------------------------
 * THE BUG #166 ADDRESSES (Mervin, 2026-05-28):
 * --------------------------------------------------------------------------
 *
 * Workorder records (operationally defined as records whose
 * `lowes_projectNo` starts with "WO") can have a meaningful `installer`
 * payroll# even after being unscheduled. Before #166, dragging such a
 * scheduled WO workorder back to the left-side RTS bucket silently
 * cleared `installer` to "" and pushed that downstream via the MongoDB
 * mutation pipeline to FileMaker/Hookdeck. The user never saw the
 * installer payroll# being wiped.
 *
 * Mirrors the #154 Repair Order protection. Same predicate shape, with the
 * discriminator changed:
 *   - #154: classification === "Repair Order"
 *   - #166: lowes_projectNo starts with "WO" (case-insensitive)
 *
 * --------------------------------------------------------------------------
 * WHY `lowes_projectNo` AND NOT `classification`:
 * --------------------------------------------------------------------------
 *
 * Forensic investigation against PROD's 24,280 ActiveQueue docs
 * (dbtools/src/queries/wo-workorder-discriminator-investigate.mjs):
 *
 *   classification === "Workorder/Expense"      → 1509 records
 *   lowes_projectNo starts with "WO" (case ins) → 1288 records
 *   BOTH                                        → only 5 records
 *   classWE without WO prefix (likely "expense"
 *     records, "PR" prefix in samples)          → 1504 records
 *   WO prefix without classWE (jobs that happen
 *     to be on a workorder project)             → 1283 records
 *
 * Mervin's operational definition is the WO prefix. Gating on
 * classification would miss 1283 affected records AND incorrectly
 * catch 1504 expense records.
 *
 * @param {Object|null|undefined} project - The dragged document (typically
 *   from the TanStack `projectListAfterMerge` cache or `scheduleArray`).
 *   Any shape may be passed; defensive against null/undefined/missing fields.
 * @returns {boolean} true if project's `lowes_projectNo` starts with "WO"
 *   (case-insensitive) AND its `installer` field holds a non-empty value
 *   (string or number); false otherwise.
 */
export function isWoWorkorderWithInstaller(project) {
  if (!project) return false;
  const projectNo = project.lowes_projectNo;
  if (projectNo === undefined || projectNo === null) return false;
  if (typeof projectNo !== "string") return false;
  // Case-insensitive prefix match — Recruiter has historically allowed
  // "wo..." lowercase too in some installer entries.
  if (projectNo.slice(0, 2).toLowerCase() !== "wo") return false;
  const installer = project.installer;
  if (installer === undefined || installer === null) return false;
  return String(installer).trim() !== "";
}
